import React from 'react';

// ─── Stage model ────────────────────────────────────────────────────────────

export type JourneyStageId =
  | 'submitted'
  | 'bidding'
  | 'selecting'
  | 'contract_funding'
  | 'work_in_progress'
  | 'final_approval'
  | 'closed';

export type StageState = 'complete' | 'current' | 'blocked' | 'upcoming' | 'disputed';
export type JourneyRole = 'homeowner' | 'contractor' | 'admin';

export interface JourneyStage {
  id: JourneyStageId;
  label: string;
  state: StageState;
  helperText?: string; // Role-aware helper shown under the current stage
}

// ─── Status → stage mapping ──────────────────────────────────────────────────

const STATUS_TO_STAGE: Record<string, JourneyStageId> = {
  DRAFT: 'submitted',
  REQUESTED: 'submitted',
  POSTED: 'submitted',
  BIDS_COLLECTING: 'bidding',
  BIDS_READY: 'bidding',
  AWAITING_REPORT: 'bidding',
  DECIDING: 'selecting',
  BID_SELECTED: 'selecting',
  HOMEOWNER_CONTRACT_SIGNING: 'contract_funding',
  ESCROW_PENDING: 'contract_funding',
  SCHEDULED: 'work_in_progress',
  ACTIVE_P1: 'work_in_progress',
  ACTIVE_P2: 'work_in_progress',
  ACTIVE_P3: 'work_in_progress',
  IN_PROGRESS: 'work_in_progress',
  COMPLETED: 'final_approval',
  CLOSED: 'closed',
  CANCELLED: 'closed',
  DISPUTED: 'work_in_progress',
};

const STAGE_ORDER: JourneyStageId[] = [
  'submitted',
  'bidding',
  'selecting',
  'contract_funding',
  'work_in_progress',
  'final_approval',
  'closed',
];

const STAGE_LABELS: Record<JourneyStageId, string> = {
  submitted: 'Submitted',
  bidding: 'Bidding',
  selecting: 'Contractor Selected',
  contract_funding: 'Contract & Funding',
  work_in_progress: 'Work In Progress',
  final_approval: 'Final Approval',
  closed: 'Closed',
};

// Role-aware helper text shown under the currently-active stage
const STAGE_HELPER: Record<JourneyStageId, Record<JourneyRole, string>> = {
  submitted: {
    homeowner: 'Your project has been submitted. We are arranging contractor bids for your review.',
    contractor: 'This project has been submitted and is awaiting measurement or bid collection.',
    admin: 'Project submitted. Awaiting measurement report or bid intake.',
  },
  bidding: {
    homeowner: 'Contractors are reviewing your project and submitting bids. You will be notified when bids are ready to compare.',
    contractor: 'Bids are being collected. Submit your bid to be considered for this project.',
    admin: 'Bid collection in progress. Publish bids when complete to move the homeowner forward.',
  },
  selecting: {
    homeowner: 'Your bids are ready. Review and compare options, then select the contractor you want to move forward with.',
    contractor: 'The homeowner is reviewing bids. You will be notified if your bid is selected.',
    admin: 'Homeowner is in bid review / contractor selection. Both parties must sign the agreement before funding.',
  },
  contract_funding: {
    homeowner: 'Almost there — your contractor is confirmed. Review and sign the agreement, then fund the project escrow to get started.',
    contractor: 'The homeowner is setting up the contract and funding the project. You will be notified when work can begin.',
    admin: 'Contract signing and escrow funding in progress. Verify completion before allowing work to start.',
  },
  work_in_progress: {
    homeowner: 'Work is underway. Your contractor will submit milestone evidence at each stage for your review and approval.',
    contractor: 'Work is active. Submit milestone evidence at each stage to unlock the next phase and release funds.',
    admin: 'Active project. Monitor milestones and escrow releases. Disputes can be opened from the project cockpit.',
  },
  final_approval: {
    homeowner: 'Work is complete. Review the final milestone, confirm you are satisfied, and close the project to release the final payment.',
    contractor: 'All milestones have been submitted. Awaiting final homeowner approval before the last payment is released.',
    admin: 'Project complete. Confirm homeowner sign-off and release final escrow payment.',
  },
  closed: {
    homeowner: 'This project is closed. Thank you for using CoreBids.',
    contractor: 'This project is closed. Completed work is reflected in your profile.',
    admin: 'Project closed.',
  },
};

// ─── Derivation ──────────────────────────────────────────────────────────────

export function deriveJourneyStages(
  projectStatus: string,
  role: JourneyRole,
  isDisputed?: boolean,
  minimumStage?: JourneyStageId,
): JourneyStage[] {
  const mappedStageId = STATUS_TO_STAGE[projectStatus] ?? 'submitted';
  const mappedIdx = STAGE_ORDER.indexOf(mappedStageId);
  const minIdx = minimumStage ? STAGE_ORDER.indexOf(minimumStage) : -1;
  // Advance to minimumStage if the inferred DB stage is behind actual progress
  const currentStageId = minIdx > mappedIdx ? minimumStage! : mappedStageId;
  const currentIdx = STAGE_ORDER.indexOf(currentStageId);

  return STAGE_ORDER.map((id, idx) => {
    let state: StageState;

    if (isDisputed && id === currentStageId) {
      state = 'disputed';
    } else if (idx < currentIdx) {
      state = 'complete';
    } else if (idx === currentIdx) {
      state = 'current';
    } else {
      state = 'upcoming';
    }

    const helperText =
      state === 'current' || state === 'disputed'
        ? STAGE_HELPER[id][role]
        : undefined;

    return {
      id,
      label: STAGE_LABELS[id],
      state,
      helperText,
    };
  });
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const STATE_COLORS: Record<StageState, { bg: string; border: string; text: string; dot: string }> = {
  complete: { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', dot: '#10b981' },
  current: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af', dot: '#2563eb' },
  blocked: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', dot: '#f59e0b' },
  upcoming: { bg: '#f3f4f6', border: '#e5e7eb', text: '#9ca3af', dot: '#d1d5db' },
  disputed: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', dot: '#ef4444' },
};

const STATE_ICON: Record<StageState, string> = {
  complete: '✓',
  current: '●',
  blocked: '!',
  upcoming: '○',
  disputed: '!',
};

// ─── Component ───────────────────────────────────────────────────────────────

interface ProjectJourneyTimelineProps {
  projectStatus: string;
  role: JourneyRole;
  isDisputed?: boolean;
  compact?: boolean; // Horizontal compact strip mode
  className?: string;
  minimumStage?: JourneyStageId; // Advance past DB status when actual progress is further
}

const ProjectJourneyTimeline: React.FC<ProjectJourneyTimelineProps> = ({
  projectStatus,
  role,
  isDisputed = false,
  compact = false,
  className,
  minimumStage,
}) => {
  const stages = deriveJourneyStages(projectStatus, role, isDisputed, minimumStage);
  const currentStage = stages.find(s => s.state === 'current' || s.state === 'disputed');

  if (compact) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          overflowX: 'auto',
          padding: '12px 0',
        }}
      >
        {stages.map((stage, idx) => {
          const colors = STATE_COLORS[stage.state];
          const isLast = idx === stages.length - 1;
          return (
            <React.Fragment key={stage.id}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 64,
                  maxWidth: 80,
                  flex: '0 0 auto',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: colors.dot,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {STATE_ICON[stage.state]}
                </div>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: stage.state === 'current' || stage.state === 'disputed' ? 700 : 400,
                    color: colors.text,
                    marginTop: 4,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    maxWidth: 72,
                  }}
                >
                  {stage.label}
                </span>
              </div>
              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    minWidth: 16,
                    background:
                      stage.state === 'complete' ? '#10b981' : '#e5e7eb',
                    marginBottom: 18,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Full vertical / card layout
  return (
    <div className={className}>
      {/* Stage chips row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0,
          overflowX: 'auto',
          marginBottom: currentStage?.helperText ? 16 : 0,
        }}
      >
        {stages.map((stage, idx) => {
          const colors = STATE_COLORS[stage.state];
          const isLast = idx === stages.length - 1;
          const isCurrent = stage.state === 'current' || stage.state === 'disputed';

          return (
            <React.Fragment key={stage.id}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 72,
                  flex: '0 0 auto',
                }}
              >
                {/* Circle */}
                <div
                  style={{
                    width: isCurrent ? 36 : 28,
                    height: isCurrent ? 36 : 28,
                    borderRadius: '50%',
                    background: colors.dot,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: isCurrent ? 14 : 11,
                    fontWeight: 700,
                    flexShrink: 0,
                    boxShadow: isCurrent ? `0 0 0 4px ${colors.border}` : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {STATE_ICON[stage.state]}
                </div>
                {/* Label */}
                <span
                  style={{
                    fontSize: isCurrent ? 11 : 10,
                    fontWeight: isCurrent ? 700 : 400,
                    color: colors.text,
                    marginTop: 6,
                    textAlign: 'center',
                    lineHeight: 1.3,
                    maxWidth: 72,
                    wordBreak: 'break-word',
                  }}
                >
                  {stage.label}
                </span>
              </div>
              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    minWidth: 12,
                    background:
                      stage.state === 'complete' ? '#10b981' : '#e5e7eb',
                    marginTop: isCurrent ? 17 : 13,
                    marginBottom: 0,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Helper text card for current stage */}
      {currentStage?.helperText && (
        <div
          style={{
            background: STATE_COLORS[currentStage.state].bg,
            border: `1px solid ${STATE_COLORS[currentStage.state].border}`,
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            color: STATE_COLORS[currentStage.state].text,
            lineHeight: 1.5,
          }}
        >
          <span style={{ fontWeight: 600 }}>{currentStage.label}: </span>
          {currentStage.helperText}
        </div>
      )}
    </div>
  );
};

export default ProjectJourneyTimeline;
